(function () {
  'use strict';

  // Each section loads this asset; register the global lifecycle listener only once.
  if (window.__swCommunityBuildsInitialized) return;
  window.__swCommunityBuildsInitialized = true;

  const API_URL =
    'https://api.reviews.io/reviews?store=switchology.io&type=store_review&with=images';
  const SECTION_SELECTOR = '[data-sw-community-builds]';
  const REQUEST_TIMEOUT_MS = 10000;
  // Requests belong to a section instance, not to the global script.
  const reviewRequests = new WeakMap();

  // REVIEWS.io may include null or invalid photo entries; accept HTTPS URLs only.
  function getPhotoUrl(photo) {
    if (typeof photo !== 'string') return null;

    try {
      const url = new URL(photo);
      return url.protocol === 'https:' ? url.href : null;
    } catch (_error) {
      return null;
    }
  }

  // The API supplies square resize URLs with the original file nested in "src".
  // Resize previews for our card/thumbnail geometry without changing the source.
  function getResizedPhotoUrl(photoUrl, width, height) {
    try {
      const url = new URL(photoUrl);
      if (url.hostname !== 'media.reviews.co.uk' ||
          url.pathname !== '/resize/create' ||
          !url.searchParams.get('src')) return photoUrl;

      url.searchParams.set('width', String(width));
      url.searchParams.set('height', String(height));
      return url.href;
    } catch (_error) {
      return photoUrl;
    }
  }

  // Full-size review photos must use the uncropped upload, not a resize variant.
  function getOriginalPhotoUrl(photoUrl) {
    try {
      const url = new URL(photoUrl);
      if (url.hostname !== 'media.reviews.co.uk' ||
          url.pathname !== '/resize/create') return photoUrl;

      const source = url.searchParams.get('src');
      if (!source) return photoUrl;

      const original = new URL(source);
      return original.protocol === 'https:' ? original.href : photoUrl;
    } catch (_error) {
      return photoUrl;
    }
  }

  // Clone the Liquid card; insert API-supplied text via textContent, never HTML.
  function renderReview(template, review, photoUrl) {
    const author =
      typeof review.author?.name === 'string' && review.author.name.trim()
        ? review.author.name.trim()
        : 'Customer';

    const card = template.content.firstElementChild.cloneNode(true);
    // The entire card acts as one button; avoid announcing the full review as its name.
    card.setAttribute('aria-label', 'Open review by ' + author);

    const image = card.querySelector('[data-review-image]');
    image.src = getResizedPhotoUrl(photoUrl, 800, 600);
    image.alt = 'Customer build photo by ' + author;

    card.querySelector('[data-review-author]').textContent = author;

    const ratingElement = card.querySelector('[data-review-rating]');
    const rating = Number(review.rating);
    if (Number.isInteger(rating) && rating >= 1 && rating <= 5) {
      ratingElement.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
      ratingElement.setAttribute('aria-label', rating + ' out of 5 stars');
    } else {
      ratingElement.remove();
    }

    const commentElement = card.querySelector('[data-review-comment]');
    if (typeof review.comments === 'string' && review.comments.trim()) {
      commentElement.textContent = review.comments.trim();
    } else {
      commentElement.remove();
    }

    return card;
  }

  // Reuse one native modal per section; the API response is already in memory.
  function openReview(root, review, photos) {
    const dialog = root.querySelector('[data-sw-review-dialog]');
    if (!dialog || dialog.open) return;

    let index = 0;
    const image = dialog.querySelector('[data-sw-dialog-image]');
    const counter = dialog.querySelector('[data-sw-dialog-counter]');
    const previous = dialog.querySelector('[data-sw-dialog-prev]');
    const next = dialog.querySelector('[data-sw-dialog-next]');
    const author = typeof review.author?.name === 'string' && review.author.name.trim()
      ? review.author.name.trim() : 'Customer';
    const rating = Number(review.rating);

    dialog.querySelector('[data-sw-dialog-author]').textContent = author;
    dialog.querySelector('[data-sw-dialog-rating]').textContent =
      Number.isInteger(rating) && rating >= 1 && rating <= 5
        ? '★'.repeat(rating) + '☆'.repeat(5 - rating) : '';
    dialog.querySelector('[data-sw-dialog-comment]').textContent =
      typeof review.comments === 'string' ? review.comments : '';

    // Match REVIEWS.io IDs (e.g. "store_review-123") to Shopify review_id values.
    const reviewId = String(review.id ?? '').replace(/^store_review-/, '');
    const links = dialog.querySelector('[data-sw-dialog-build-links]');
    const buildsSection = dialog.querySelector('[data-sw-dialog-builds]');
    const buildsTemplate = root.querySelector('[data-sw-review-build-links]');

    links.replaceChildren();
    if (buildsTemplate) {
      buildsTemplate.content.querySelectorAll('[data-sw-build-review-id]').forEach(function (entry) {
        if (entry.dataset.swBuildReviewId !== reviewId) return;
        const link = entry.querySelector('a');
        if (link) links.appendChild(link.cloneNode(true));
      });
    }
    buildsSection.hidden = !links.childElementCount;
    dialog.querySelector('[data-sw-dialog-builds-heading]').textContent =
      links.childElementCount === 1 ? 'Customize this build' : 'Customize these builds';

    const navigation = dialog.querySelector('[data-sw-dialog-navigation]');
    const thumbnails = dialog.querySelector('[data-sw-dialog-thumbnails]');
    thumbnails.replaceChildren();
    navigation.hidden = thumbnails.hidden = photos.length < 2;

    function showPhoto() {
      image.src = getOriginalPhotoUrl(photos[index]);
      image.alt = 'Customer build photo ' + (index + 1) + ' of ' + photos.length + ' by ' + author;
      counter.textContent = (index + 1) + ' / ' + photos.length;
      thumbnails.querySelectorAll('button').forEach(function (button, position) {
        const active = position === index;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });
    }

    if (photos.length > 1) {
      photos.forEach(function (photoUrl, position) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sw-community-builds__dialog-thumbnail';
        button.setAttribute('aria-label', 'Show photo ' + (position + 1) + ' of ' + photos.length);
        const thumbnail = document.createElement('img');
        thumbnail.src = getResizedPhotoUrl(photoUrl, 200, 150);
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        button.appendChild(thumbnail);
        button.addEventListener('click', function () {
          index = position;
          showPhoto();
        });
        thumbnails.appendChild(button);
      });
    }

    previous.onclick = function () {
      index = (index - 1 + photos.length) % photos.length;
      showPhoto();
    };
    next.onclick = function () {
      index = (index + 1) % photos.length;
      showPhoto();
    };

    showPhoto();
    dialog.showModal();
  }

  function initializeDialog(root) {
    const dialog = root.querySelector('[data-sw-review-dialog]');
    if (!dialog || dialog.dataset.initialized) return;
    dialog.dataset.initialized = 'true';

    dialog.querySelector('[data-sw-dialog-close]').addEventListener('click', function () {
      dialog.close();
    });
    // Backdrop clicks target the dialog, not its content.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  // Article HTML is rendered by Liquid and cloned only when its card is opened.
  function initializeBlogDialog(root) {
    const dialog = root.querySelector('[data-sw-blog-dialog]');
    if (!dialog || dialog.dataset.initialized) return;
    dialog.dataset.initialized = 'true';

    const content = dialog.querySelector('[data-sw-blog-content]');
    const footer = dialog.querySelector('[data-sw-blog-footer]');
    root.addEventListener('click', function (event) {
      const trigger = event.target.closest('a[data-sw-blog-open]');
      if (!trigger || !root.contains(trigger) || dialog.open ||
          event.defaultPrevented || event.button !== 0 ||
          event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
          trigger.target === '_blank' || trigger.hasAttribute('download')) return;

      const template = trigger.closest('.sw-community-builds__card--blog')
        ?.querySelector('[data-sw-blog-detail]');
      if (!template) return;

      const detail = template.content.cloneNode(true);
      const title = detail.querySelector('.sw-community-builds__article-heading h3');
      if (!title) return; // Preserve the article link if a dialog cannot be labelled.
      title.id = dialog.getAttribute('aria-labelledby');
      content.replaceChildren(detail);
      footer.replaceChildren();
      const builds = content.querySelector('.sw-community-builds__article-builds');
      if (builds && builds.querySelector('.sw-community-builds__build-links a')) {
        footer.appendChild(builds);
        footer.hidden = false;
      } else {
        footer.hidden = true;
      }
      content.scrollTop = 0;
      dialog.showModal();
      // Cancel native navigation only after a usable dialog opens.
      event.preventDefault();
    });

    dialog.querySelector('[data-sw-blog-close]').addEventListener('click', function () {
      dialog.close();
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  // Mix Liquid build stories with reviews and paginate the sidebar.
  function renderSidebar(root, container, reviewCards) {
    const stories = Array.from(container.children).filter(function (element) {
      return element.classList.contains('sw-community-builds__card--blog');
    });
    const cards = document.createDocumentFragment();
    for (let i = 0; i < Math.max(stories.length, reviewCards.length); i++) {
      if (stories[i]) cards.appendChild(stories[i]);
      if (reviewCards[i]) cards.appendChild(reviewCards[i]);
    }
    if (!cards.hasChildNodes()) return false;

    container.removeAttribute('role');
    container.removeAttribute('aria-live');
    container.replaceChildren(cards);

    const items = Array.from(container.children);
    const controls = root.querySelector('[data-sw-sidebar-controls]');
    if (!controls) return true;
    const previous = controls.querySelector('[data-sw-sidebar-prev]');
    const next = controls.querySelector('[data-sw-sidebar-next]');
    const counter = controls.querySelector('[data-sw-sidebar-counter]');
    const pageCount = Math.ceil(items.length / 2);
    let page = 0;

    function showPage(value) {
      page = Math.max(0, Math.min(value, pageCount - 1));
      items.forEach(function (item, i) {
        item.hidden = i < page * 2 || i >= page * 2 + 2;
      });
      counter.textContent = (page * 2 + 1) + '–' +
        Math.min(page * 2 + 2, items.length) + ' / ' + items.length;
      previous.disabled = page === 0;
      next.disabled = page === pageCount - 1;
    }
    controls.hidden = pageCount <= 1;
    previous.onclick = function () { showPage(page - 1); };
    next.onclick = function () { showPage(page + 1); };
    showPage(0);
    return true;
  }

  // Keep blog stories and photo reviews in one horizontally scrollable track.
  function renderCarousel(root, container, reviewCards) {
    const stories = Array.from(container.children).filter(function (element) {
      return element.classList.contains('sw-community-builds__card--blog');
    });
    const cards = document.createDocumentFragment();
    for (let i = 0; i < Math.max(stories.length, reviewCards.length); i++) {
      if (stories[i]) cards.appendChild(stories[i]);
      if (reviewCards[i]) cards.appendChild(reviewCards[i]);
    }
    if (!cards.hasChildNodes()) return false;

    container.removeAttribute('role');
    container.removeAttribute('aria-live');
    container.replaceChildren(cards);

    const controls = root.querySelector('[data-sw-carousel-controls]');
    if (!controls) return true;
    const previous = controls.querySelector('[data-sw-carousel-prev]');
    const next = controls.querySelector('[data-sw-carousel-next]');

    function updateControls() {
      const end = container.scrollWidth - container.clientWidth;
      controls.hidden = end <= 2;
      previous.disabled = container.scrollLeft <= 2;
      next.disabled = container.scrollLeft >= end - 2;
    }

    function move(direction) {
      const items = Array.from(container.children);
      const viewport = container.getBoundingClientRect();
      const tolerance = 3;
      const target = direction > 0
        ? items.find(function (item) {
            return item.getBoundingClientRect().left > viewport.left + tolerance;
          })
        : items.slice().reverse().find(function (item) {
            return item.getBoundingClientRect().left < viewport.left - tolerance;
          });
      if (!target) return;
      const offset = target.getBoundingClientRect().left - viewport.left;
      container.scrollBy({
        left: offset,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }

    previous.onclick = function () { move(-1); };
    next.onclick = function () { move(1); };
    container.addEventListener('scroll', updateControls, { passive: true });
    if (root.swCarouselObserver) root.swCarouselObserver.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      root.swCarouselObserver = new ResizeObserver(updateControls);
      root.swCarouselObserver.observe(container);
    }
    updateControls();
    return true;
  }

  // Each review becomes one card, using its first valid photo.
  function renderReviews(root, container, template, reviews) {
    const reviewCards = [];

    for (const review of reviews) {
      if (!review || typeof review !== 'object' || Array.isArray(review)) continue;
      const photos = Array.isArray(review.photos) ? review.photos : [];
      const validPhotos = photos.map(getPhotoUrl).filter(Boolean);

      if (validPhotos.length) {
        const card = renderReview(template, review, validPhotos[0]);
        card.addEventListener('click', function () {
          openReview(root, review, validPhotos);
        });
        card.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openReview(root, review, validPhotos);
          }
        });
        reviewCards.push(card);
      }
    }

    if (root.dataset.mode === 'sidebar') return renderSidebar(root, container, reviewCards);
    if (root.dataset.mode === 'carousel') return renderCarousel(root, container, reviewCards);
    if (!reviewCards.length) return false;

    const cards = document.createDocumentFragment();
    if (root.dataset.mode === 'gallery') {
      // The first Liquid blog card stays featured above this container.
      // Move the remaining blog cards between the reviews in actual DOM order.
      const stories = Array.from(root.children).filter(function (element) {
        return element.classList.contains('sw-community-builds__card--blog');
      }).slice(1);
      const interval = Math.max(1, Math.ceil(reviewCards.length / (stories.length + 1)));
      let storyIndex = 0;

      reviewCards.forEach(function (card, index) {
        cards.appendChild(card);
        if ((index + 1) % interval === 0 && storyIndex < stories.length) {
          cards.appendChild(stories[storyIndex++]);
        }
      });
      while (storyIndex < stories.length) {
        cards.appendChild(stories[storyIndex++]);
      }
    } else {
      reviewCards.forEach(function (card) {
        cards.appendChild(card);
      });
    }

    // Announce loading/errors, but not all reviews as live content.
    container.removeAttribute('role');
    container.removeAttribute('aria-live');
    container.replaceChildren(cards);
    return true;
  }

  async function loadReview(root) {
    // Each section instance should make at most one request per rendered lifecycle.
    if (root.dataset.swReviewState) return;

    const container = root.querySelector('[data-sw-review-container]');
    const template = root.querySelector('[data-sw-review-template]');
    if (!container || !template) return;

    root.dataset.swReviewState = 'loading';
    const controller = new AbortController();
    const request = { controller, unloaded: false };
    reviewRequests.set(root, request);
    const timeout = setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      const url = new URL(API_URL);
      // Fetch all current photo reviews in gallery mode, without enlarging sidebar requests.
      url.searchParams.set('per_page', root.dataset.mode === 'gallery' ? '50' : root.dataset.mode === 'carousel' ? '30' : '5');
      // A blank tag means all photo reviews in both gallery and sidebar mode.
      const tag = root.dataset.reviewTag?.trim();
      if (tag) url.searchParams.set('tag', tag);

      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) {
        throw new Error('REVIEWS.io returned HTTP ' + response.status);
      }

      const payload = await response.json();
      if (!Array.isArray(payload.reviews)) {
        throw new Error('Invalid REVIEWS.io response: reviews array missing');
      }

      // The Theme Editor may replace this section while the request is in flight.
      if (request.unloaded || !root.isConnected || controller.signal.aborted) return;

      if (!renderReviews(root, container, template, payload.reviews)) {
        root.dataset.swReviewState = 'empty';
        if (root.dataset.mode === 'carousel') {
          // No reviews or blog cards: hide the heading and carousel together.
          root.hidden = true;
        } else {
          container.textContent = 'No customer photo reviews found.';
        }
        return;
      }

      root.dataset.swReviewState = 'ready';
      if (root.dataset.mode === 'carousel') root.hidden = false;
    } catch (error) {
      // An editor unload must not render a fallback or log an API error.
      if (request.unloaded || !root.isConnected) return;

      const hasFallback = root.dataset.mode === 'sidebar'
        ? renderSidebar(root, container, [])
        : root.dataset.mode === 'carousel'
          ? renderCarousel(root, container, [])
          : false;
      if (hasFallback) {
        root.dataset.swReviewState = 'fallback';
        if (root.dataset.mode === 'carousel') root.hidden = false;
      } else {
        root.dataset.swReviewState = 'error';
        if (root.dataset.mode === 'carousel') {
          // Suppress an empty homepage widget, including its heading.
          root.hidden = true;
        } else {
          container.textContent = 'Customer reviews are unavailable right now.';
        }
      }
      console.error('[SW Community Builds] Could not load reviews:', error);
    } finally {
      clearTimeout(timeout);
      if (reviewRequests.get(root) === request) reviewRequests.delete(root);
    }
  }

  // The scope can be the document or a section re-rendered by Shopify.
  function initialize(scope) {
    function setup(root) {
      initializeDialog(root);
      initializeBlogDialog(root);
      loadReview(root);
    }
    if (scope.matches?.(SECTION_SELECTOR)) setup(scope);
    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initialize(document);
    });
  } else {
    initialize(document);
  }

  // Shopify replaces section markup when settings change in the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const roots = event.target.matches?.(SECTION_SELECTOR)
      ? [event.target]
      : Array.from(event.target.querySelectorAll?.(SECTION_SELECTOR) || []);
    roots.forEach(function (root) {
      root.swCarouselObserver?.disconnect();
      const request = reviewRequests.get(root);
      if (request) {
        request.unloaded = true;
        request.controller.abort();
        reviewRequests.delete(root);
      }
    });
  });
})();
